import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import BrandLockup from "@/v2/components/BrandLockup";
import { errText } from "@/v2/lib/hooks";
import "@/v2/v2.css";
import { Eye, EyeOff, ShieldCheck, UserPlus, ArrowLeft } from "lucide-react";
import storyPhoto from "@/v2/assets/official/nabawi_gate.jpg";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", password: "", confirm: "", work_type: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.name.trim().length < 3) return setError("اكتب اسمك الكامل (3 أحرف على الأقل)");
    if (!f.work_type) return setError("اختر طبيعة عملك: ميداني أو مكتبي");
    if (f.password.length < 6) return setError("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
    if (f.password !== f.confirm) return setError("كلمة المرور وتأكيدها غير متطابقين");
    setLoading(true);
    try {
      const res = await register(f.name.trim(), f.password, f.work_type);
      if (res.renamed) toast.info(`اسم الدخول الخاص بك: ${res.login_name}`, { duration: 8000 });
      toast.success("تم إنشاء حسابك — أكمل بياناتك الآن");
      navigate("/onboarding", { replace: true });
    } catch (err) {
      setError(errText(err, "تعذّر إنشاء الحساب، حاول مرة أخرى."));
    } finally { setLoading(false); }
  };

  return (
    <div className="v2-root brand-auth min-h-[100dvh] grid lg:grid-cols-2" dir="rtl">
      <div className="brand-auth-story theme-navy relative hidden lg:flex flex-col justify-end gap-8 p-12 overflow-hidden">
        <img src={storyPhoto} alt="" aria-hidden="true" className="absolute inset-x-0 top-0 w-full h-[58%] object-cover" />
        <div className="absolute z-10 top-12 right-12"><BrandLockup tone="dark" /></div>
        <div className="relative z-10">
          <p className="font-display text-[2.25rem] leading-[1.35] mb-3 text-ink">حسابك الجديد<br />خطوتين فقط</p>
          <p className="text-ink leading-relaxed max-w-md text-lg opacity-90">حدّد طبيعة عملك وسجّل بياناتك لنخصّص لك مسارًا يناسب وظيفتك.</p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-ink-2 text-sm"><ShieldCheck size={16} className="text-ok" /> بيئة آمنة — منصة داخلية للموظفين</div>
      </div>

      <div className="brand-auth-main flex items-center justify-center p-6 sm:p-12">
        <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          onSubmit={submit} data-testid="register-form" className="w-full max-w-md">
          <div className="lg:hidden mb-8"><BrandLockup tone="light" /></div>
          <h1 className="mb-2">إنشاء حساب جديد</h1>
          <p className="text-ink-2 mb-8">سجّل باسمك وكلمة المرور، وسنكمل الباقي معك.</p>

          {error && <div data-testid="register-error" className="mb-4 rounded-2xl bg-danger/15 border border-danger/40 text-danger px-4 py-3 text-sm">{error}</div>}

          <label htmlFor="work-type" className="block text-sm font-semibold mb-2 text-ink-2">طبيعة عملك</label>
          <select id="work-type" data-testid="register-work-type" value={f.work_type} required onChange={e => setF({ ...f, work_type: e.target.value })} className="v2-input mb-5">
            <option value="">اختر طبيعة العمل</option><option value="field">ميداني</option><option value="office">مكتبي</option>
          </select>
          <label className="block text-sm font-semibold mb-2 text-ink-2">الاسم الثلاثي</label>
          <input data-testid="register-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
            placeholder="مثال: أحمد عبدالله المالكي" className="v2-input !h-13 mb-5" />

          <label className="block text-sm font-semibold mb-2 text-ink-2">كلمة المرور</label>
          <div className="relative mb-5">
            <input data-testid="register-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}
              type={show ? "text" : "password"} className="v2-input !pe-12" />
            <button type="button" onClick={() => setShow(!show)} className="absolute inset-y-0 left-3 flex items-center text-ink-2">{show ? <EyeOff size={20} /> : <Eye size={20} />}</button>
          </div>

          <label className="block text-sm font-semibold mb-2 text-ink-2">تأكيد كلمة المرور</label>
          <input data-testid="register-confirm" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })}
            type={show ? "text" : "password"} className="v2-input mb-7" />

          <button data-testid="register-submit" type="submit" disabled={loading} className="v2-btn w-full">
            <UserPlus size={18} /> {loading ? "جارٍ إنشاء الحساب..." : "إنشاء الحساب"}
          </button>

          <Link to="/login" data-testid="register-to-login"
            className="mt-6 flex items-center justify-center gap-2 text-sm text-ink-2 hover:text-ink">
            لديك حساب؟ تسجيل الدخول <ArrowLeft size={15} />
          </Link>
        </motion.form>
      </div>
    </div>
  );
}
